import moment from 'moment'
import { sortBy, property } from 'lodash'
import { customerHelper } from '@tweddle-se-team/common/constants'
import { customerId } from '@/utils/env'

const { CUSTOMER_IDS } = customerHelper

const overviewMapper = {
  [CUSTOMER_IDS.TME]: {
    queryParams: ['selectedTab', 'selectedView', 'selectedPart', 'searchValue', 'selectedLineOffYear']
  },
  [CUSTOMER_IDS.SUBARU]: {
    queryParams: ['selectedTab', 'selectedView', 'selectedPart', 'selectedTopicType']
  },
  [CUSTOMER_IDS.NORDICS]: {
    queryParams: ['selectedTab', 'selectedView', 'selectedPart', 'selectedTopicType']
  }
}[customerId]

const _buildHotspotFilters = (query, searchFrom, searchTo) => {
  const { brand, model, modelType } = query
  const filters = { brand, model, modelType }
  if (searchFrom && searchTo) filters.lineOffDate = [searchFrom, searchTo]
  return filters
}

const _getHotspotSearchRange = productionDate => {
  const searchFrom = moment(productionDate).subtract(3, 'year').toISOString()
  const searchTo = moment(productionDate).add(1, 'year').set('month', 0).set('date', 31).toISOString()
  return { searchFrom, searchTo }
}

export const getClosestHotspotForModel = async (query, productionDate, get) => {
  const { searchFrom, searchTo } = _getHotspotSearchRange(productionDate)
  const hotspotFilters = _buildHotspotFilters(query, searchFrom, searchTo)
  const apiQuery = {
    page: 0,
    limit: 0,
    isPublished: true,
    filters: JSON.stringify(hotspotFilters)
  }
  const hotspots = await get('hotspots', apiQuery)
  return findClosestHotspot(hotspots?.data, productionDate)
}

// Find the closest hotspot to a given production date
export const findClosestHotspot = (hotspots, productionDate) => {
  if (!hotspots || !hotspots.length || !productionDate) return null
  const productionDateObj = moment.utc(productionDate)
  return hotspots.reduce((acc, hotspot) => {
    const hotspotDate = moment.utc(hotspot.lineOffDate)
    const accDate = moment.utc(acc.lineOffDate)
    const hotspotDiff = Math.abs(hotspotDate.diff(productionDateObj))
    const accDiff = Math.abs(accDate.diff(productionDateObj))
    return hotspotDiff < accDiff ? hotspot : acc
  })
}

// Returns tag to resource keys map for a given year and baseDFOMEngPublications object
export const getTagToResourceKeysEng = baseDFOMEngPublication => {
  const topics = !baseDFOMEngPublication || !baseDFOMEngPublication.topics ? [] : baseDFOMEngPublication.topics
  return topics.reduce((acc, publicationTopic) => {
    publicationTopic.tags?.forEach(tag => {
      acc[tag] = acc[tag] || []
      acc[tag].push(publicationTopic.resourceKey)
    })
    return acc
  }, {})
}

// Returns tag to resource keys map for a given year and baseDFOMEngPublications object
export const getTagToResourceKeysEngMap = (baseDFOMEngPublications, lineOffYear) => {
  const baseDFOMEngPublication =
    baseDFOMEngPublications && baseDFOMEngPublications[lineOffYear]
      ? baseDFOMEngPublications[lineOffYear]
      : { topics: [] }
  return getTagToResourceKeysEng(baseDFOMEngPublication)
}

// Factory for createNotAvailablePublication callback
export const createNotAvailablePublicationFactory = (publicationTypes, t, renamePubType) => (publicationType, id) => ({
  disabled: true,
  publicationType,
  type: 'publication',
  id: id,
  title: `${t(renamePubType(publicationTypes, publicationType, 'label'))} ${t('is_not_available')} `
})

export const DEFAULT_OPTIONS = {
  selectedTab: 'explore',
  selectedView: 'Front'
}

export const getYearOfDate = date => {
  const d = new Date(date)
  return String(d.getFullYear())
}

// Determine if there is a DFOM for the selected line off year (if no year is selected upon initial load, take first year from allLineOffYears)
export const hasDFOM = (DFOMLineOffYears, allLineOffYears, selectedLineOffYear) => {
  const year = selectedLineOffYear || getYearOfDate(allLineOffYears[0])
  return DFOMLineOffYears.includes(year)
}

export const getContentGroupedByPartTags = ({
  hotspotMapping,
  selectedView,
  tagToResourceKeysEngMap,
  publicationTopics,
  publicationToHotspot,
  createNotAvailablePublication
}) => {
  const spots = hotspotMapping?.mapping?.[selectedView]?.spots || []
  const tagsForView = spots.filter(s => s.tag).map(s => s.tag)
  const publicationsForView = spots.filter(s => s.publication).map(s => s.publication)

  const tagParts = tagsForView.reduce((acc, tag) => {
    const resourceKeys = tagToResourceKeysEngMap[tag] || []
    acc[tag] = publicationTopics.filter(topic => resourceKeys.includes(topic.resourceKey))
    return acc
  }, {})

  const publicationParts = publicationsForView.reduce((acc, publication, i) => {
    const publicationFound = publicationToHotspot.find(p => p.publicationType === publication)
    acc[publication] = [publicationFound || createNotAvailablePublication(publication, i)]
    return acc
  }, {})

  return { ...tagParts, ...publicationParts }
}

export const getOverviewQueryParams = query =>
  Object.entries(query).reduce((acc, [qsParam, value]) => {
    if (overviewMapper?.queryParams?.includes(qsParam) && value) {
      acc[qsParam] = value
    }
    return acc
  }, {})

export const createMapByLineOffDate = (data = [], acceptYears = false) => {
  const map = new Map()
  const sortedData = sortBy(data, property('partNumber'))

  for (const item of sortedData) {
    const lineOffYear = getYearOfDate(item.lineOffDate)
    if (acceptYears !== false && !acceptYears.includes(lineOffYear)) {
      continue
    }
    if (map.has(lineOffYear)) {
      const existingItem = map.get(lineOffYear)
      const newItemLineOffDate = new Date(item.lineOffDate)
      const existingLineOffDate = new Date(existingItem.lineOffDate)
      if (newItemLineOffDate - existingLineOffDate > 0) {
        map.delete(lineOffYear)
        map.set(lineOffYear, item)
      }
    } else {
      map.set(lineOffYear, item)
    }
  }

  return map.size ? Object.fromEntries(map) : {}
}
